import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SelectField, NONE } from "@/lib/crm";
import { ITV_TYPES } from "@/lib/interventions";
import { PRIORITIES } from "@/components/activities/ActivityDialog";
import { CompanyPicker } from "@/components/sales/CompanyPicker";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const F = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;
const init = (itv, company) => ({ title: itv?.title || "", description: itv?.description || "", type: itv?.type || "assistenza", priority: itv?.priority || "normal",
  site: itv?.site || "", company: company || (itv && { id: itv.company_id, name: itv.company_name }) || null, contact_id: itv?.contact_id || "", lead_technician_id: "" });

export const InterventionDialog = ({ open, onOpenChange, itv, company, onSaved }) => {
  const { can } = useAuth();
  const [f, setF] = useState(init(itv, company));
  const [contacts, setContacts] = useState([]);
  const [busy, setBusy] = useState(false);
  const request = !can("interventions:create");
  const assigner = !itv && can("interventions:assign");
  const [techs, setTechs] = useState([]);
  useEffect(() => { if (open && assigner) api.get("/interventions/meta").then((r) => setTechs(r.data.technicians || [])).catch(() => setTechs([])); }, [open, assigner]);
  useEffect(() => { if (open) setF(init(itv, company)); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!f.company?.id) { setContacts([]); return; }
    api.get(`/interventions/companies/${f.company.id}/contacts`).then((r) => setContacts(r.data.items.map((c) => ({ key: c.id, label: c.name })))).catch(() => setContacts([]));
  }, [f.company?.id]);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));
  const save = async () => {
    setBusy(true);
    const body = { title: f.title, description: f.description || null, type: f.type, priority: f.priority, site: f.site || null, contact_id: f.contact_id || null };
    try {
      const r = itv ? await api.patch(`/interventions/${itv.id}`, body) : await api.post("/interventions", { ...body, company_id: f.company.id, ...(assigner ? { lead_technician_id: f.lead_technician_id } : {}) });
      toast.success(itv ? "Intervento aggiornato" : request ? `Richiesta ${r.data.number} inviata al Responsabile Tecnici` : `Intervento ${r.data.number} creato`);
      onOpenChange(false); onSaved?.(r.data.id || itv.id);
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="itv-dialog">
        <DialogHeader>
          <DialogTitle>{itv ? "Modifica intervento" : request ? "Richiesta di intervento" : "Nuovo intervento"}</DialogTitle>
          {!itv && !assigner && <DialogDescription>Verrà assegnato al Responsabile Tecnici, che lo smisterà ai suoi tecnici.</DialogDescription>}
        </DialogHeader>
        <div className="space-y-3">
          {!itv && <CompanyPicker value={f.company} onChange={set("company")} disabled={!!company} testId="itv-company" source="/interventions/companies" />}
          <F label="Titolo"><Input value={f.title} onChange={set("title")} maxLength={200} data-testid="itv-input-title" /></F>
          <div className="grid grid-cols-2 gap-3">
            <SelectField label="Tipologia" value={f.type} onChange={set("type")} options={ITV_TYPES} testId="itv-input-type" />
            <SelectField label={request ? "Priorità proposta" : "Priorità"} value={f.priority} onChange={set("priority")} options={PRIORITIES} testId="itv-input-priority" />
          </div>
          {assigner && <SelectField label="Tecnico assegnato (obbligatorio)" value={f.lead_technician_id} onChange={set("lead_technician_id")} options={techs} placeholder="Seleziona il tecnico" testId="itv-input-technician" />}
          <SelectField label="Contatto" value={f.contact_id || NONE} onChange={(v) => set("contact_id")(v === NONE ? "" : v)} options={contacts} allowNone placeholder="Nessun contatto" testId="itv-input-contact" />
          <F label="Sede / indirizzo (vuoto = indirizzo del cliente)"><Input value={f.site} onChange={set("site")} maxLength={300} data-testid="itv-input-site" /></F>
          <F label="Descrizione del problema"><Textarea rows={4} value={f.description} onChange={set("description")} maxLength={4000} data-testid="itv-input-description" /></F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={save} disabled={busy || !f.title.trim() || !f.company || (assigner && !f.lead_technician_id)} data-testid="itv-save">{itv ? "Salva" : request ? "Invia richiesta" : "Crea intervento"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
